import React, { useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Grid, Stack, styled, SxProps } from '@mui/material'

import {
  PaginatedDocumentInstance,
  SignatureDocumentElementFieldValue,
} from '../../types'
import BackButton from '../BackButton'
import ButtonWithLoader from '../buttons/ButtonWithLoader'
import { ProgressBar } from '../ProgressBar/ProgressBar'
import Text from '../Text'
import { ConsentFormPage, ConsentFormPageHandle } from './ConsentFormPage'

const StyledContainer = styled(Stack)`
  padding: ${({ theme }) => theme.spacing(1.5)};
  flex-grow: 1;
  overflow-y: auto;
`

const StyledButtonContainer = styled(Grid)`
  border-top: ${({ theme }) => theme.constants.tableBorder};
  padding: ${({ theme }) => theme.spacing(1.5)};
`

export interface ConsentFormProps {
  readonly containerSxProps?: SxProps
  readonly forms: PaginatedDocumentInstance[]
  readonly onFieldChange?: (
    formIndex: number,
    pageIndex: number,
  ) => (
    elementIndex: number,
    value:
    | string
    | string[]
    | boolean
    | SignatureDocumentElementFieldValue
    | null,
  ) => void
  readonly onSubmit?: (formIndex: number, isFinalStep: boolean) => void
  readonly readOnly?: boolean
}

export function ConsentForm({
  containerSxProps,
  forms,
  onSubmit,
  onFieldChange,
  readOnly,
}: ConsentFormProps) {
  const [currentFormIndex, setCurrentFormIndex] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [errorPages, setErrorPages] = useState<number[]>([])
  const { t } = useTranslation('Common')

  const formPageRef = useRef<ConsentFormPageHandle>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const totalSteps = forms.reduce((acc, form) => acc + form.pages.length, 0)

  const pagesBeforeCurrentForm = forms
    .slice(0, currentFormIndex)
    .reduce((acc, form) => acc + form.pages.length, 0)

  const currentStep = pagesBeforeCurrentForm + currentPage

  const currentForm = forms[currentFormIndex]
  const currentFormTotalPages = currentForm.pages.length

  const isLastPageOfCurrentForm = currentPage === currentFormTotalPages
  const isLastPageOfAllForms = currentStep === totalSteps

  const checkValidationErrors = () => {
    const isValid = formPageRef.current?.validate()

    const updatedErrorPages = isValid
      ? errorPages.filter(page => page !== currentPage) // Remove the current page from errorPages if it's valid
      : errorPages.includes(currentPage)
        ? errorPages
        : [...errorPages, currentPage] // Add the current page to errorPages if it's invalid

    setErrorPages(updatedErrorPages)
    return updatedErrorPages
  }

  const handleContinueClick = () => {
    containerRef?.current?.scrollTo(0, 0)
    checkValidationErrors()
    if (currentPage < currentFormTotalPages) {
      setCurrentPage(currentPage + 1)
    } else if (currentFormIndex < forms.length - 1) {
      setCurrentFormIndex(currentFormIndex + 1)
      setCurrentPage(1)
    }
  }

  const handleSubmit = () => {
    containerRef?.current?.scrollTo(0, 0)
    const updatedErrorPages = checkValidationErrors()

    if (updatedErrorPages.length) {
      flushSync(() => {
        setCurrentPage(updatedErrorPages[0])
      })
      formPageRef.current?.validate()
    } else if (currentFormIndex < forms.length - 1) {
      onSubmit?.(currentFormIndex, false)
      setCurrentFormIndex(currentFormIndex + 1)
      setCurrentPage(1)
    } else {
      onSubmit?.(currentFormIndex, true)
    }
  }

  return (
    <>
      <StyledContainer ref={containerRef} spacing={3} sx={containerSxProps}>
        <Text variant="h2">{currentForm.name}</Text>
        <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        <ConsentFormPage
          documentElements={currentForm.pages[currentPage - 1]}
          key={`${currentFormIndex}-${currentPage}`}
          readOnly={readOnly}
          ref={formPageRef}
          onFieldChange={onFieldChange?.(currentFormIndex, currentPage - 1)}
        />
      </StyledContainer>
      <StyledButtonContainer container>
        {currentPage > 1 ? (
          <Grid item mr={1}>
            <BackButton
              label={t('Common:BACK_ACTION')}
              onClick={() => {
                setCurrentPage(page => page - 1)
              }}
            />
          </Grid>
        ) : null}
        <ButtonWithLoader
          disabled={isLastPageOfAllForms && !onSubmit}
          style={{ width: 200, marginLeft: 'auto' }}
          onClick={isLastPageOfCurrentForm ? handleSubmit : handleContinueClick}
        >
          {isLastPageOfAllForms ? 'Finish' : 'Save & continue'}
        </ButtonWithLoader>
      </StyledButtonContainer>
    </>
  )
}
